import { FC, useState } from "react";
import { Table, Button, Modal, Form, Input, InputNumber, Switch, Popconfirm } from "antd";
import { Plus, Edit, Trash2, Package } from "lucide-react";
import { useGetActiveInventoryTemplates } from "~/services/inventory-templates/inventory-templates.queries";
import { inventoryTemplatesApi } from "~/services/inventory-templates/inventory-templates.api";
import { useNotification } from "~/hooks";

const { TextArea } = Input;

export const ScreenInventoryTemplates: FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [form] = Form.useForm();
  const { success, error } = useNotification();

  const { data: templates, isLoading, refetch } = useGetActiveInventoryTemplates();

  const handleAdd = () => {
    setEditingTemplate(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEdit = (record: any) => {
    setEditingTemplate(record);
    form.setFieldsValue(record);
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingTemplate) {
        await inventoryTemplatesApi.update(editingTemplate.id, values);
      } else {
        await inventoryTemplatesApi.create(values);
      }

      success({ message: editingTemplate ? 'Template updated' : 'Template created' });
      setIsModalOpen(false);
      refetch();
    } catch (err: any) {
      error({ message: err.message || 'Failed to save template' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await inventoryTemplatesApi.delete(id);
      success({ message: 'Template deleted' });
      refetch();
    } catch (err: any) {
      error({ message: err.message || 'Failed to delete template' });
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "20%",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "25%",
      render: (text: string) => text || "-",
    },
    {
      title: "Dimensions (L×W×H)",
      key: "dimensions",
      width: "15%",
      render: (_: any, record: any) => {
        if (record.length || record.width || record.height) {
          return `${record.length || 0}" × ${record.width || 0}" × ${record.height || 0}"`;
        }
        return "-";
      },
    },
    {
      title: "Cubic Feet",
      key: "cubicFeet",
      width: "10%",
      render: (_: any, record: any) => {
        if (record.length && record.width && record.height) {
          const cubicFeet = (record.length * record.width * record.height) / 1728;
          return `${cubicFeet.toFixed(2)} ft³`;
        }
        return "-";
      },
    },
    {
      title: "Weight",
      dataIndex: "weight",
      key: "weight",
      width: "8%",
      render: (weight: number) => weight ? `${weight} lbs` : "-",
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      width: "8%",
      render: (isActive: boolean) => (
        <span className={`px-2 py-1 rounded-full text-xs ${isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
          {isActive ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "7%",
      render: (_: any, record: any) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleEdit(record)}
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
          >
            <Edit size={16} />
          </button>
          <Popconfirm
            title="Delete this template?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <button className="p-1.5 text-red-600 hover:bg-red-50 rounded">
              <Trash2 size={16} />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package size={28} />
            Inventory Templates
          </h1>
          <p className="text-gray-500 mt-1">
            Manage template items for quick estimate creation
          </p>
        </div>
        <Button
          type="primary"
          icon={<Plus size={16} />}
          onClick={handleAdd}
        >
          Add Template
        </Button>
      </div>

      <div className="bg-white rounded-lg border shadow-sm">
        <Table
          dataSource={templates || []}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 20 }}
        />
      </div>

      <Modal
        title={editingTemplate ? "Edit Template" : "Add Template"}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={() => setIsModalOpen(false)}
        width={600}
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="e.g., Small 2D Work, Medium Box" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Optional description" />
          </Form.Item>

          <div className="grid grid-cols-4 gap-4">
            <Form.Item name="length" label="Length (in)">
              <InputNumber min={0} className="w-full" />
            </Form.Item>
            <Form.Item name="width" label="Width (in)">
              <InputNumber min={0} className="w-full" />
            </Form.Item>
            <Form.Item name="height" label="Height (in)">
              <InputNumber min={0} className="w-full" />
            </Form.Item>
            <Form.Item name="weight" label="Weight (lbs)">
              <InputNumber min={0} className="w-full" />
            </Form.Item>
          </div>

          <Form.Item name="isActive" label="Active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ScreenInventoryTemplates;
